import { useActivity, useUpdateActivityStatus } from "@/lib/api/activities";
import { usePostHogEvent } from "@/lib/posthog";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseResponderSendOptions {
  entityId: string;
  activityType: string;
  isOpen: boolean;
  canSend: boolean;
  markInProgressOnOpen?: boolean;
  buildResult: () => Record<string, unknown>;
  buildExtraEventProps?: () => Record<string, unknown>;
  onClose: () => void;
}

export function useResponderSend({
  entityId,
  activityType,
  isOpen,
  canSend,
  markInProgressOnOpen = false,
  buildResult,
  buildExtraEventProps,
  onClose,
}: UseResponderSendOptions) {
  const { data: activity } = useActivity(entityId);
  const statusMutation = useUpdateActivityStatus();
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const [isSending, setIsSending] = useState(false);
  const markInProgress = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    if (!markInProgressOnOpen) return;
    if (!isOpen) {
      markInProgress.current = null;
      return;
    }
    if (markInProgress.current || activity?.status !== "sent") return;
    markInProgress.current = statusMutation
      .mutateAsync({ entityId, status: "in_progress" })
      .then(
        () => true,
        () => false,
      );
  }, [
    markInProgressOnOpen,
    isOpen,
    activity?.status,
    entityId,
    statusMutation,
  ]);

  const handleSend = useCallback(async () => {
    if (!canSend || isSending || activity?.status === "completed") return;
    setIsSending(true);
    try {
      const alreadyInProgress = markInProgress.current
        ? await markInProgress.current
        : false;
      if (!alreadyInProgress && activity?.status === "sent") {
        await statusMutation.mutateAsync({ entityId, status: "in_progress" });
      }
      await statusMutation.mutateAsync({
        entityId,
        status: "completed",
        result: buildResult(),
      });
      dispatchEvent("activity.mentor.response_sent", {
        entityId,
        activityType,
        seconds_since_teen_sent: activity
          ? Math.round(
              (Date.now() - new Date(activity.createdAt).getTime()) / 1000,
            )
          : null,
        ...buildExtraEventProps?.(),
      });
      onClose();
    } catch {
      // surfaced by the mutation's error toast; leave the modal open to retry
    } finally {
      setIsSending(false);
    }
  }, [
    canSend,
    isSending,
    activity,
    statusMutation,
    entityId,
    activityType,
    buildResult,
    buildExtraEventProps,
    dispatchEvent,
    onClose,
  ]);

  return { activity, isSending, handleSend };
}
